import React, { useEffect, useState } from "react";
import { Col, Form, Row, notification } from "antd";
import { useAppSelector, useAppDispatch } from "../../hooks/reduxHook";
import {
  useListDevicesQuery,
  useScheduleDeviceUpdateMutation,
  useLazyGetDeviceByIdQuery,
} from "../../actions/devicesInQuery";
import { unselectDevice } from "../../reducers/devicesSlice";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../config/routes";
import DeviceSelector from "./DeviceSelector";
import UpdateButton from "./UpdateButton";
import { SettingFilled } from "@ant-design/icons";
import "./index.css";

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();

  const { devices, selected } = useAppSelector((state) => ({
    devices: state.devices?.devices || {},
    selected: state.devices?.selected,
  }));

  const { isLoading: listLoading } = useListDevicesQuery();
  const [triggerGetDevice] = useLazyGetDeviceByIdQuery();
  const [scheduleUpdate] = useScheduleDeviceUpdateMutation();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selected?.uuid) {
      navigate(ROUTES.admin);
    }
    form.setFieldValue("device", {
      value: selected?.uuid || "",
      label: <span>{selected?.display_name || ""}</span>,
    });
  }, [navigate, selected, form]);

  useEffect(() => {
    if (listLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [listLoading]);

  useEffect(() => {
    const devicesWithUpdate = Object.values(devices)
      .filter((item) => item.update_available)
      .map((item) => item.display_name);

    if (devicesWithUpdate.length > 0) {
      api["warning"]({
        message: "Update Available",
        description: (
          <>
            <p>Firmware update is available for the following devices:</p>
            <span>{devicesWithUpdate.join(", ")}</span>
          </>
        ),
      });
    }
  }, [devices, api]);

  const handleSelect = async (value: string) => {
    if (!value) {
      dispatch(unselectDevice());
      return;
    }
    setLoading(true);
    try {
      await triggerGetDevice(value).unwrap();
    } catch (error) {
      console.error("Failed to select device", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickUpdate = async () => {
    if (selected?.uuid) {
      await scheduleUpdate({ deviceUuid: selected.uuid, will_update: true });
    }
  };

  const handleCancelUpdate = async () => {
    if (selected?.uuid) {
      await scheduleUpdate({ deviceUuid: selected.uuid, will_update: false });
    }
  };

  return (
    <>
      {contextHolder}

      <Row gutter={4} align="top" justify={"center"} wrap={false}>
        <Col span={21}>
          <DeviceSelector
            loading={loading}
            form={form}
            handleSelect={handleSelect}
            devices={devices}
          />
        </Col>
        {selected?.display_name && (
          <Col span={1}>
            <SettingFilled
              onClick={() => navigate(ROUTES.device_settings)}
              style={{ fontSize: "2rem", cursor: "pointer" }}
            />
          </Col>
        )}
        <Col span={2}>
          <UpdateButton
            selected={selected}
            handleClickUpdate={handleClickUpdate}
            handleCancelUpdate={handleCancelUpdate}
          />
        </Col>
      </Row>
    </>
  );
};

export default Index;
